import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowLeft, Play } from "lucide-react";
import content from "../seo/content.json";
import useSeo from "../seo/useSeo";

const meta = content.pages.find((p) => p.path === "/faq");

/**
 * Standalone FAQ page.
 *
 * The same questions render inside the landing page, but a dedicated URL gives
 * the long-tail question queries their own indexable target and gives AI answer
 * engines a single page to cite. Copy lives in src/seo/content.json so this and
 * scripts/prerender.js can never disagree.
 */
export default function Faq() {
  const [open, setOpen] = useState(0);

  useSeo("/faq");

  return (
    <div className="min-h-[100dvh] bg-white pb-20">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <Link
            to="/"
            data-testid="faq-back"
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 shrink-0"
            aria-label="Back to home"
          >
            <ArrowLeft size={18} />
          </Link>
          <h1 className="text-lg font-black text-slate-900 truncate">
            CarTrack FAQ
          </h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 py-8">
        <p className="text-[15px] text-slate-600 leading-[1.7]">{meta.intro}</p>

        <div className="mt-8 space-y-3">
          {content.faqs.map((f, idx) => {
            const isOpen = open === idx;
            return (
              <div
                key={f.q}
                className="border border-slate-200 rounded-2xl overflow-hidden bg-white hover:border-indigo-200 transition-colors"
              >
                <h2>
                  <button
                    onClick={() => setOpen(isOpen ? null : idx)}
                    aria-expanded={isOpen}
                    className="w-full px-5 py-4 flex items-center justify-between text-left gap-4 group cursor-pointer"
                  >
                    <span className="text-[14px] sm:text-[15px] font-bold text-slate-900 group-hover:text-indigo-600 transition-colors leading-snug">
                      {f.q}
                    </span>
                    <span
                      className={`w-7 h-7 rounded-full border border-slate-200 flex items-center justify-center shrink-0 transition-transform duration-300 ${
                        isOpen ? "rotate-180 border-indigo-500 bg-indigo-50" : ""
                      }`}
                    >
                      <ChevronDown
                        size={13}
                        className={isOpen ? "text-indigo-600" : "text-slate-400"}
                      />
                    </span>
                  </button>
                </h2>
                {/* Always in the DOM so the answer text is crawlable and
                    copy-searchable; visibility is purely presentational. */}
                <div className={isOpen ? "block" : "hidden"}>
                  <p className="px-5 pb-5 pt-1 text-[13px] sm:text-[14px] text-slate-600 leading-[1.75] border-t border-slate-100 mt-0">
                    {f.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 rounded-2xl bg-slate-900 p-8 text-center">
          <h2 className="text-[22px] font-black text-white leading-tight">
            Start tracking your car for free
          </h2>
          <p className="mt-3 text-[14px] text-slate-300 leading-relaxed">
            No GPS device to buy, no subscription. Set up on your Android head
            unit in about five minutes.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white text-slate-900 text-[14px] font-bold hover:bg-slate-100 transition-colors w-full sm:w-auto"
            >
              Create a free account
            </Link>
            <a
              href={content.site.playUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-white/15 bg-white/5 text-white text-[14px] font-semibold hover:bg-white/10 transition-colors w-full sm:w-auto"
            >
              <Play size={14} className="fill-current" />
              Get it on Google Play
            </a>
          </div>
        </div>

        <nav className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[13px] text-slate-500">
          <Link to="/" className="hover:text-indigo-600">
            Car GPS tracker app
          </Link>
          <Link to="/user-manual" className="hover:text-indigo-600">
            Setup guide
          </Link>
          <Link to="/about-us" className="hover:text-indigo-600">
            About CarTrack
          </Link>
          <Link to="/privacy-policy" className="hover:text-indigo-600">
            Privacy policy
          </Link>
        </nav>
      </div>
    </div>
  );
}
